import { useCallback, useState } from "react";

import { useValidateCustomFormLazyQuery } from "@/apollo/types";
import { Alert, AlertIcon, AlertTitle } from "@/components/elements/Alert";
import { Button, IconButton } from "@/components/elements/Button";
import Select, { AppliedMultiSelect } from "@/components/elements/Select";
import { InputDescription } from "@/components/primitives/InputDescription";
import FieldLabel from "@/components/primitives/InputLabel";
import { Input } from "@/components/primitives/input";
import { DropdownOption } from "@/integrations";
import { XIcon } from "lucide-react";

import { RedditAdsReport } from "./RedditAdsForm";

export default function RedditAdsCustomReport(props: {
  report: RedditAdsReport;
  onChangeReport: (report: RedditAdsReport) => void;
  onRemoveReport: (report: RedditAdsReport) => void;
  breakdownOptions: DropdownOption<string>[];
  fieldOptions: DropdownOption<string>[];
  timezoneOptions: DropdownOption<string>[];
  connectionId: string;
  selectedAdAccounts?: DropdownOption<string>[];
}) {
  const {
    report,
    onChangeReport,
    onRemoveReport,
    breakdownOptions,
    fieldOptions,
    timezoneOptions,
    connectionId,
    selectedAdAccounts,
  } = props;

  const [validationSuccess, setValidationSuccess] = useState(false);
  const [validationCallError, setValidationCallError] = useState<string | null>(
    null,
  );
  const [callValidationQuery, { loading: validationQueryLoading }] =
    useValidateCustomFormLazyQuery();

  const runValidation = useCallback(
    async (validateViaApi: boolean = false) => {
      const queryData = {
        id: report.id,
        name: report.name,
        breakdowns: report.breakdowns.map((x) => x.value),
        fields: report.fields.map((x) => x.value),
        timezone: report.timezone?.value,
      };

      const { data, error } = await callValidationQuery({
        variables: {
          input: {
            connectionId,
            payload: JSON.stringify({
              report: queryData,
              selectedAdAccounts,
              validateViaApi,
            }),
          },
        },
      });

      if (error) {
        setValidationSuccess(false);
        setValidationCallError(error.message);
        return;
      }

      const result = data?.validateCustomForm.customFormValidationResult;
      if (!result) {
        setValidationSuccess(false);
        setValidationCallError("Validation failed");
        return;
      }

      const isValid =
        result.valid === true ||
        (result.apiValidated === true && !result.apiError);

      if (isValid) {
        setValidationSuccess(true);
        setValidationCallError(null);
      } else {
        setValidationSuccess(false);
        setValidationCallError(
          result.message || result.apiErrorMessage || "Validation failed",
        );
      }
    },
    [callValidationQuery, connectionId, report, selectedAdAccounts],
  );

  const changeReport = (nextReport: RedditAdsReport) => {
    onChangeReport(nextReport);
    setValidationSuccess(false);
  };

  return (
    <div className="relative rounded-xs border bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700">
      <div className="pb-4">
        <FieldLabel id={`name-${report.id}`} required={true}>
          Name
        </FieldLabel>
        <Input
          placeholder="Name of your report"
          value={report.name}
          onChange={(e) => changeReport({ ...report, name: e.target.value })}
        />
      </div>
      <div className="pb-4">
        <FieldLabel id={`breakdowns-${report.id}`} required={true}>
          Breakdowns
        </FieldLabel>
        <AppliedMultiSelect
          isMulti={true}
          isLoading={false}
          placeholder="Select one or more"
          onChange={(values: DropdownOption<string>[]) =>
            changeReport({ ...report, breakdowns: values })
          }
          isOptionDisabled={() => report.breakdowns.length >= 3}
          options={breakdownOptions}
          value={report.breakdowns}
          hideSelectedOptions={false}
        />
        <InputDescription className="m-1">
          Select up to 3 breakdowns (or 4 only with COUNTRY + REGION).
        </InputDescription>
      </div>

      <div className="pb-4">
        <FieldLabel id={`fields-${report.id}`} required={true}>
          Fields
        </FieldLabel>
        <AppliedMultiSelect
          isMulti={true}
          isLoading={false}
          placeholder="Select one or more"
          onChange={(values: DropdownOption<string>[]) =>
            changeReport({ ...report, fields: values })
          }
          options={fieldOptions}
          value={report.fields}
          hideSelectedOptions={false}
        />
        <InputDescription className="m-1">
          Select one or more fields to include in the sync. If you select a
          conversions field, the report should run in Full sync because
          conversion numbers keep updating over time.
        </InputDescription>
      </div>

      <div>
        <FieldLabel id={`timezone-${report.id}`} required={true}>
          Timezone
        </FieldLabel>
        <Select
          isMulti={false}
          isLoading={validationQueryLoading}
          placeholder="Select one"
          onChange={(value: DropdownOption<string> | null) =>
            changeReport({ ...report, timezone: value ?? undefined })
          }
          options={timezoneOptions}
          value={report.timezone}
          hideSelectedOptions={false}
        />
        <InputDescription className="m-1">
          Choose the timezone used for Reddit Ads reporting.
        </InputDescription>
      </div>

      <div className="absolute top-0 right-0 p-2">
        <IconButton
          size="xs"
          icon={<XIcon />}
          variant="ghost"
          onClick={() => onRemoveReport(report)}
        />
      </div>
      <div className="mt-4">
        <Button
          size="sm"
          variant="solid"
          colorScheme="primary"
          onClick={() => runValidation(true)}
          isLoading={validationQueryLoading}
        >
          Validate report
        </Button>
        {validationSuccess ? (
          <div className="mt-2">
            <Alert status="success">
              <AlertIcon />
              Report is valid
            </Alert>
          </div>
        ) : null}
        {validationCallError ? (
          <div className="mt-2">
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Error:</AlertTitle>
              {validationCallError}
            </Alert>
          </div>
        ) : null}
        {!validationCallError && !validationSuccess ? (
          <div className="mt-2">
            <Alert status="info">
              <AlertIcon />
              Validate your report to continue
            </Alert>
          </div>
        ) : null}
      </div>
    </div>
  );
}
